import React from 'react';
import { useHistory } from 'react-router-dom';
import WidgetWrapper from './WidgetWrapper';
import tagService from '../services/tagService';
import { useWorkflowExecution } from '../hooks/useWorkflowExecution';

const Button = ({
  text = 'Button',
  variant = 'contained',
  disabled = false,
  onClick,
  tagId = null,
  tagValue = null, // For enable/disable based on tag value
  isEditMode = false, // Disable actions in edit mode
  // Action configuration
  actionType = 'none', // 'none', 'tag-operation', 'navigation', 'start-task', 'workflow'
  // Tag Operation properties
  targetTagId = null,
  tagOperation = 'set', // 'set', 'toggle', 'increment', 'decrement'
  tagSetValue = '',
  tagIncrementValue = 1,
  // Navigation properties  
  navigateTo = '',
  openInNewTab = false,
  // Task properties
  taskCommand = '',
  taskParameters = '',
  // Workflow properties (new production system)
  workflowName = '',
  workflowParameters = {},
  companyId = 1,
  enableLogging = false,
  watchdogTimeout = null,
  // Style properties
  backgroundColor,
  foreColor,
  borderWidth,
  borderColor,
  borderRadius,
  fontSize,
  fontWeight,
  fontFamily,
  opacity,
  ...props
}) => {
  const history = useHistory();
  const { executeWorkflow, isExecuting, error } = useWorkflowExecution();
  
  // Use tag value for button state if available
  const isDisabled = disabled || isEditMode || isExecuting || (tagId && tagValue !== null && !tagValue);

  const handleClick = async (e) => {
    if (onClick) {
      onClick(e);
    }

    // Skip actions in edit mode
    if (isEditMode) {
      return;
    }

    // Execute action based on actionType
    try {
      switch (actionType) {
        case 'tag-operation':
          await executeTagOperation();
          break;
        case 'navigation':
          executeNavigation();
          break;
        case 'start-task':
          executeStartTask();
          break;
        case 'workflow':
          await executeWorkflowAction();
          break;
        default:
          // No action
          break;
      }
    } catch (error) {
      console.error('Button action execution failed:', error);
      // You might want to show a user notification here
    }
  };

  const executeTagOperation = async () => {
    if (!targetTagId) {
      return;
    }

    const tag = await tagService.getTag(targetTagId);
    if (!tag) {
      console.error(`Tag with ID ${targetTagId} not found`);
      return;
    }

    let newValue;
    switch (tagOperation) {
      case 'set':
        newValue = tagSetValue;
        break;
      case 'toggle':
        newValue = !tag.value;
        break;
      case 'increment':
        newValue = (parseFloat(tag.value) || 0) + (parseFloat(tagIncrementValue) || 1);
        break;
      case 'decrement':
        newValue = (parseFloat(tag.value) || 0) - (parseFloat(tagIncrementValue) || 1);
        break;
      default:
        return;
    }

    await tagService.updateTagValue(targetTagId, newValue);
  };

  const executeNavigation = () => {
    if (!navigateTo) {
      return;
    }

    // Extract page ID from navigateTo value
    let targetPageId = null;

    // Check if navigateTo is directly a page ID or page name
    if (!isNaN(navigateTo)) {
      targetPageId = navigateTo;
    } else if (navigateTo.includes('/scada/view/')) {
      // Extract from URL format like "/scada/view/92"
      const match = navigateTo.match(/\/scada\/view\/(\d+)/);
      if (match) {
        targetPageId = match[1];
      }
    } else if (navigateTo.includes('id=')) {
      // Extract from URL format like "/scada/design?id=2"
      const urlParams = new URLSearchParams(navigateTo.split('?')[1]);
      targetPageId = urlParams.get('id');
    }

    // If we have a page ID, just update the dropdown selection
    // This will be handled by a custom event
    if (targetPageId) {
      // Dispatch a custom event that PreviewMode will listen to
      window.dispatchEvent(new CustomEvent('scada-page-select', {
        detail: { pageId: Number(targetPageId) }
      }));
      return;
    }

    // For external URLs (if needed in the future)
    if (navigateTo.startsWith('http://') || navigateTo.startsWith('https://')) {
      if (openInNewTab) {
        window.open(navigateTo, '_blank');
      } else {
        window.location.href = navigateTo;
      }
    }
  };

  const executeWorkflowAction = async () => {
    if (!workflowName) {
      return;
    }

    try {

      const result = await executeWorkflow(
        workflowName,
        workflowParameters,
        companyId,
        enableLogging,
        watchdogTimeout
      );


      // Show success notification
      if (window.showNotification) {
        window.showNotification(`Workflow "${workflowName}" completed successfully`, 'success');
      } else {
        alert(`Workflow "${workflowName}" completed successfully`);
      }

      return result;

    } catch (error) {
      console.error('Failed to execute production workflow:', error);

      // Show error notification
      if (window.showNotification) {
        window.showNotification(`Failed to execute workflow "${workflowName}": ${error.message}`, 'error');
      } else {
        alert(`Failed to execute workflow "${workflowName}": ${error.message}`);
      }

      throw error;
    }
  };

  const executeStartTask = async () => {
    if (!taskCommand) {
      return;
    }

    try {
      // Parse parameters if provided
      let parsedParameters = {};
      if (taskParameters) {
        try {
          parsedParameters = typeof taskParameters === 'string'
            ? JSON.parse(taskParameters)
            : taskParameters;
        } catch (error) {
          parsedParameters = { data: taskParameters };
        }
      }


      // Use production workflow execution system with polling
      const result = await executeWorkflow(
        taskCommand,
        parsedParameters,
        companyId || 1,
        enableLogging || false,
        watchdogTimeout || null
      );


      // Show success notification
      if (window.showNotification) {
        window.showNotification(`Workflow "${taskCommand}" completed successfully`, 'success');
      } else {
        alert(`Workflow "${taskCommand}" completed successfully`);
      }

      return result;

    } catch (error) {
      console.error('Failed to execute production workflow:', error);

      // Show error notification
      if (window.showNotification) {
        window.showNotification(`Failed to execute workflow "${taskCommand}": ${error.message}`, 'error');
      } else {
        alert(`Failed to execute workflow "${taskCommand}": ${error.message}`);
      }

      throw error;
    }
  };

  return (
    <WidgetWrapper
      backgroundColor={backgroundColor}
      foreColor={foreColor}
      borderWidth={borderWidth}
      borderColor={borderColor}
      borderRadius={borderRadius}
      fontSize={fontSize}
      fontWeight={fontWeight}
      fontFamily={fontFamily}
      opacity={opacity}
      {...props}
    >
      <div
        onClick={isDisabled ? undefined : handleClick}
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: isDisabled ? 'default' : 'pointer',
          opacity: isDisabled ? 0.6 : 1,
          userSelect: 'none',
        }}
      >
        <span>{isExecuting ? 'Executing...' : text}</span>
      </div>
    </WidgetWrapper>
  );
};

export default Button;